/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Wine, Coins, TrendingUp, TrendingDown, Tag, MapPin, Grape, Clock, AlertTriangle, BarChart3, PieChart as PieIcon, LineChart as LineIcon, Boxes, ArrowLeft, Lock } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart, Bar,
  PieChart, Pie, Cell,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { apiClient } from "@/lib/api";
import { resolveBackHref } from "@/lib/navTracker";
import { getAnalyticsProfileForType } from "@/lib/analyticsProfiles";
import { useCollections } from "@/context/CollectionContext";
import { useAuth } from "@/context/AuthContext";
import { itemQty, computeItemValuation } from "@/utils/itemValue";
import { aggregateDashboardTotals } from "@/lib/dashboardAggregators";
import BottomNav from "@/components/BottomNav";
import EmptyDashboard from "@/components/EmptyDashboard";

import { fmt2, sumQ2 } from "@/lib/money";

const fmtMoney = (n) => `$${fmt2(n)}`;

// Palette for the pie chart slices. CDI Light Brand Pass: drives from the
// muted-analytics tokens so light + dark themes track automatically.
const PIE_COLORS = [
  "hsl(var(--c-gold))",   // #D4B46A — Muted Gold
  "hsl(var(--c-green))",  // #6FA57A — Muted Green
  "hsl(var(--c-blue))",   // #4A6FA5 — Muted Blue
  "hsl(var(--c-red))",    // #B85C5C — Muted Red
  "hsl(var(--gold))",     // Brand Bronze Gold
];

// Value-distribution buckets (per spec: <$20, $20–$50, $50–$100, $100+).
const VALUE_BUCKETS = [
  { key: "lt20",    label: "<$20",        min: 0,    max: 20    },
  { key: "20_50",   label: "$20–$50",     min: 20,   max: 50    },
  { key: "50_100",  label: "$50–$100",    min: 50,   max: 100   },
  { key: "100plus", label: "$100+",       min: 100,  max: Infinity },
];

/**
 * Dashboard — high-level metrics for the Wine collection.
 *
 * All cards read from the existing items / categories / collections data and
 * deep-link into the Items list using the URL-backed filter system that
 * Items.jsx already supports (collection_id, category_id, region, grape,
 * dwpmin/dwpmax/dwemax).  No new filter types are introduced.
 */
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { collections, loading: collectionsLoading } = useCollections();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, b, s] = await Promise.all([
          apiClient.get("/items"),
          apiClient.get("/categories"),
          apiClient.get("/sales"),
        ]);
        if (!cancelled) {
          setItems(a.data || []);
          setCategories(b.data || []);
          setSales(s.data || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [location.key]);

  // === Select Collection dropdown — drives all charts + KPIs + summary
  // metrics on the Dashboard (Task 2A/2B/2C). Initialises to the user's
  // Wine collection if one exists (legacy default), else the first
  // collection in the account. Empty state when zero collections.
  const [selectedCollectionId, setSelectedCollectionId] = useState("");

  // === Analytics Profile (Task D) ========================================
  // Resolve the analytics profile for the currently-selected collection's
  // `type_id`. The profile drives:
  //   • KPI / heading copy (Bottles vs Coins vs Items …)
  //   • Which type-specific premium charts to surface as locked placeholders
  // For legacy collections created BEFORE `type_id` existed (currently
  // `type_id === null`), we softly infer a type from the collection name so
  // existing Wine / Whisky / Rum users don't lose their rich charts on
  // first load. New collections always carry an explicit `type_id`.
  const currentProfile = useMemo(() => {
    const sel = collections.find((c) => c.id === selectedCollectionId);
    const explicit = sel?.type_id;
    if (explicit) return getAnalyticsProfileForType(explicit);
    const n = (sel?.name || "").toLowerCase();
    let inferred = null;
    if (/wine/.test(n)) inferred = "wine";
    else if (/whisky|whiskey/.test(n)) inferred = "whisky";
    else if (/spirit/.test(n)) inferred = "spirits";
    return getAnalyticsProfileForType(inferred);
  }, [collections, selectedCollectionId]);
  // No auth in the app yet — Premium charts always render locked. When
  // auth lands, replace this with the authenticated user's plan flag.
  const isPremium = !!user?.premium_plan;
  useEffect(() => {
    if (selectedCollectionId) return;
    if (collections.length === 0) return;
    const wine = collections.find((c) => (c.name || "").toLowerCase().includes("wine"));
    setSelectedCollectionId((wine || collections[0]).id);
  }, [collections, selectedCollectionId]);

  // === Selected Collection scope (Task 2C — wire charts/KPIs to picker) ===
  // The dashboard now scopes ALL data (charts + KPIs + summary metrics) to
  // the collection picked by the dropdown above. Falls back to the full
  // item set only when no collection is selected (e.g. zero collections in
  // the account — first-run edge-case). Heading + deep-link helpers use
  // the same selected collection so the experience stays consistent.
  const scopedItems = useMemo(
    () => (selectedCollectionId
      ? items.filter((it) => it.collection_id === selectedCollectionId)
      : items),
    [items, selectedCollectionId]
  );
  // Backwards-compatible alias: KPI rollups below were already keyed on
  // `kpiScopedItems`; keep the name so existing useMemo deps stay stable.
  const kpiScopedItems = scopedItems;

  // ---- KPI rollups (selected collection) ----------------------------------
  // Six top-of-page KPIs are wired to the picker; everything else (charts,
  // category / region / grape rollups, drinking-window matrix, P&L series)
  // continues to read from `scopedItems` (Wine) until Task 2C.
  // ---- Valuation lookup (new industry-standard formula, 2026-02).
  // Precompute { itemId → valuation } once so every downstream reducer
  // reads the same numbers the backend `_compute_item_valuation` would
  // return. Sales are grouped by `item_id` up-front to avoid an O(N·S)
  // scan per useMemo.
  const salesByItem = useMemo(() => {
    const map = new Map();
    for (const s of sales) {
      const k = s.item_id;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    }
    return map;
  }, [sales]);
  const valuations = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      map.set(it.id, computeItemValuation(it, salesByItem.get(it.id) || []));
    }
    return map;
  }, [items, salesByItem]);
  const _val = (it) => valuations.get(it?.id) || { current_value: 0, total_cost: 0, total_PL: 0, realised_PL: 0, unrealised_PL: 0, remaining_qty: 0 };

  const kpis = useMemo(() => {
    // Bottle count sums per-item `itemQty` so items with no quantity
    // set contribute 0 (matching the qty rule everywhere else). The old
    // `.length` treated every legacy row as a single bottle and
    // inflated the top-of-page total.
    const totalBottles = kpiScopedItems.reduce((acc, it) => acc + itemQty(it), 0);
    // 2026-02-26 — count of items whose canonical valuation qualifies as
    // "counted" (`includeInValuationTotals`). Drives the dash-profit
    // "Awaiting Valuation" placeholder when the scoped set has zero
    // valued items.
    const valuedItemsCount = kpiScopedItems.reduce(
      (acc, it) => acc + (_val(it).includeInValuationTotals ? 1 : 0),
      0,
    );
    // All monetary rollups come from the shared industry-standard
    // aggregator so Dashboard, Collection, and Portfolio Analytics can
    // never disagree.
    const scopedValuations = kpiScopedItems.map((it) => _val(it));
    const agg = aggregateDashboardTotals(scopedValuations, kpiScopedItems);
    const totalValue      = agg.collectionValue;
    const totalCost       = agg.totalCost;
    const realisedGains   = agg.realisedGains;
    const unrealisedGains = agg.unrealisedGains;
    const totalPL         = agg.totalPL;
    const plPercent       = agg.plPercent;
    const avgBottleValue  = agg.avgBottleValue;
    const unrealisedLoss  = agg.unrealisedLoss;
    const profit          = totalValue - totalCost; // Dashboard P/L: value ex GST − cost ex GST
    const currentYear = new Date().getFullYear();
    const readyNow = kpiScopedItems.filter((it) => {
      const p = Number(it.item_data?.["Drinking Window Peak"]);
      return p && !Number.isNaN(p) && p >= currentYear - 1 && p <= currentYear + 1;
    }).length;
    const pastEnd = kpiScopedItems.filter((it) => {
      const e = Number(it.item_data?.["Drinking Window End"]);
      return e && !Number.isNaN(e) && e < currentYear;
    }).length;
    return {
      totalBottles,
      valuedItemsCount,
      totalValue,
      totalCost,
      realisedGains,
      unrealisedGains,
      totalPL,
      plPercent,
      avgBottleValue,
      unrealisedLoss,
      profit,
      readyNow,
      pastEnd,
      currentYear,
    };
  }, [kpiScopedItems, valuations]);

  // ---- Aggregations -------------------------------------------------------
  const stats = useMemo(() => {
    // Same rule as the top-of-page KPI — bottle count sums itemQty so
    // legacy rows without a quantity contribute 0.
    const totalBottles = scopedItems.reduce((acc, it) => acc + itemQty(it), 0);
    const scopedValuations = scopedItems.map((it) => _val(it));
    const agg = aggregateDashboardTotals(scopedValuations, scopedItems);
    const totalValue = agg.collectionValue;
    const totalCost  = agg.totalCost;
    const profit     = agg.totalPL;

    const byCategory = new Map();
    scopedItems.forEach((it) => {
      // Bottle-weighted rollup — items with no quantity contribute 0
      // and are omitted so an empty "Wine (1)" pill can't appear when
      // TOTAL BOTTLES = 0.
      const q = itemQty(it);
      if (q <= 0) return;
      const k = it.category_id || "__uncat__";
      byCategory.set(k, (byCategory.get(k) || 0) + q);
    });
    const catRows = Array.from(byCategory.entries())
      .map(([id, count]) => ({
        id,
        count,
        name:
          id === "__uncat__"
            ? "Uncategorised"
            : categories.find((c) => c.id === id)?.category_name || "Unknown",
      }))
      .sort((a, b) => b.count - a.count);

    const byRegion = new Map();
    scopedItems.forEach((it) => {
      const r = it.item_data?.Region;
      if (!r) return;
      const q = itemQty(it);
      if (q <= 0) return;
      byRegion.set(r, (byRegion.get(r) || 0) + q);
    });
    const topRegions = Array.from(byRegion.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const byGrape = new Map();
    scopedItems.forEach((it) => {
      const g = it.item_data?.["Grape(s)"];
      const q = itemQty(it);
      if (q <= 0) return;
      // Multi-select grapes split the bottle count evenly per varietal —
      // a 6-pack of a Cab/Merlot blend contributes 3 bottles to each
      // grape so the total stays at 6. Single-varietal items get the
      // full count. Rounded to 2 dp to keep the axis tidy.
      const push = (v, portion) => {
        if (!v) return;
        byGrape.set(v, (byGrape.get(v) || 0) + portion);
      };
      if (Array.isArray(g) && g.length > 0) {
        const per = q / g.length;
        g.forEach((v) => push(v, Math.round(per * 100) / 100));
      } else if (g) {
        push(g, q);
      }
    });
    const topGrapes = Array.from(byGrape.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const currentYear = new Date().getFullYear();
    const readyNow = scopedItems.filter((it) => {
      const p = Number(it.item_data?.["Drinking Window Peak"]);
      return p && !Number.isNaN(p) && p >= currentYear - 1 && p <= currentYear + 1;
    }).length;
    const pastEnd = scopedItems.filter((it) => {
      const e = Number(it.item_data?.["Drinking Window End"]);
      return e && !Number.isNaN(e) && e < currentYear;
    }).length;

    // ----- Chart data --------------------------------------------------
    // Bar — categories. Show up to top 8; lump anything beyond into "Other"
    // (still navigable via category_id="__OTHER__" sentinel? — there's no
    // such filter, so the "Other" bucket is non-clickable. The top 8 are
    // sufficient for typical wine cellars; a 9th is rare and the lump bar
    // signals "there's more in the list card below".)
    const CAT_LIMIT = 8;
    const catBarHead = catRows.slice(0, CAT_LIMIT).map((r) => ({
      id: r.id, name: r.name, count: r.count, clickable: true,
    }));
    const catBarTail = catRows.slice(CAT_LIMIT);
    const catBar = catBarTail.length === 0
      ? catBarHead
      : [...catBarHead, {
          id: "__OTHER__",
          name: `Other (${catBarTail.length})`,
          count: catBarTail.reduce((acc, r) => acc + r.count, 0),
          clickable: false,
        }];

    // Bar — top 5 regions (re-uses the already-computed list).
    const regionBar = topRegions.map((r) => ({ name: r.name, count: r.count }));

    // Donut — top 5 grapes (re-uses already-computed list).
    const grapePie = topGrapes.map((g) => ({ name: g.name, count: g.count }));

    // Bar — value distribution. Uses NZD-normalised value so foreign
    // items bucket alongside NZD items on a single monetary scale.
    // Items with no value at all (cost AND value both empty) are skipped.
    const valueBar = VALUE_BUCKETS.map((b) => ({ ...b, count: 0 }));
    scopedItems.forEach((it) => {
      const v = _val(it).current_value;
      if (!v || v <= 0) return;
      const hit = valueBar.find((b) => v >= b.min && v < b.max);
      if (hit) hit.count += 1;
    });

    // Line — Drinking Window timeline. Count bottles by Peak year. We render
    // a continuous year axis from min(Peak) to max(Peak) so the line shows
    // gaps as zeroes instead of jumping. If no Peak data exists, the array
    // is empty and the chart card shows the empty state.
    const peakCounts = new Map();
    scopedItems.forEach((it) => {
      const p = Number(it.item_data?.["Drinking Window Peak"]);
      if (!p || Number.isNaN(p)) return;
      peakCounts.set(p, (peakCounts.get(p) || 0) + 1);
    });
    let dwTimeline = [];
    if (peakCounts.size > 0) {
      const years = Array.from(peakCounts.keys()).sort((a, b) => a - b);
      const lo = years[0];
      const hi = years[years.length - 1];
      for (let y = lo; y <= hi; y += 1) {
        dwTimeline.push({ year: y, count: peakCounts.get(y) || 0 });
      }
    }

    // Bar — Vintage Value Distribution (2026-02 chart-logic fix).
    // X-axis: vintage year, read STRICTLY from `item_data.Vintage`
    //   (never inferred from Item Name, Category, or any other
    //   field — top-level `it.vintage` and lowercase
    //   `item_data.vintage` fallbacks removed per spec).
    // Y-axis: total current valuation per vintage year — sums the
    //   canonical per-item `current_value` already computed by
    //   `computeItemValuation` and cached in the `valuations` map.
    //   No recalculation, no mutation, no normalisation.
    // Items with a non-numeric / blank vintage are dropped. Items
    //   with zero / missing valuation are dropped so a vintage year
    //   never surfaces as a synthetic zero bar. If more than 20
    //   vintages remain, keep the top 20 by value; still sorted
    //   ascending by year on the X-axis (chronological plotting).
    const vintageField = currentProfile.id === "whisky" ? "Distillation Year" : "Vintage";
    const vintageValueMap = new Map();
    scopedItems.forEach((it) => {
      const raw = it.item_data?.[vintageField];
      if (raw === null || raw === undefined || raw === "") return;
      const n = Number(raw);
      if (!Number.isFinite(n)) return;
      const yr = Math.trunc(n);
      const v = Number(_val(it).current_value);
      if (!Number.isFinite(v) || v <= 0) return;
      vintageValueMap.set(yr, (vintageValueMap.get(yr) || 0) + v);
    });
    let vintageBar = Array.from(vintageValueMap.entries())
      .map(([year, value]) => ({ year, value }));
    const totalVintages = vintageBar.length;
    if (totalVintages > 20) {
      vintageBar = [...vintageBar]
        .sort((a, b) => b.value - a.value)
        .slice(0, 20);
    }
    vintageBar.sort((a, b) => a.year - b.year);

    // Bar — Drinking Window Timeline. Count bottles by their Drinking Window
    // Start year. Reads both modern top-level fields (item.drink_window_start
    // / item.drink_window_end) AND the legacy schema variants stored under
    // `item_data` ("Drink Window Start" per the new spec AND "Drinking Window
    // Start" which is the convention already in production data). Rule: if a
    // valid start year exists, use it; if only an end year exists, the item is
    // ignored (per spec). Invalid / non-numeric values are always dropped.
    // Truncation matches the Vintage Distribution chart: if > 20 distinct
    // years exist, keep the top 20 BY COUNT, then re-sort ascending by year.
    const dwStartCounts = new Map();
    scopedItems.forEach((it) => {
      const startRaw =
        it.drink_window_start ??
        it.item_data?.["Drink Window Start"] ??
        it.item_data?.["Drinking Window Start"];
      if (startRaw === null || startRaw === undefined || startRaw === "") return;
      const n = Number(startRaw);
      if (!Number.isFinite(n)) return;
      const yr = Math.trunc(n);
      dwStartCounts.set(yr, (dwStartCounts.get(yr) || 0) + 1);
    });
    let dwStartBar = Array.from(dwStartCounts.entries())
      .map(([year, count]) => ({ year, count }));
    const totalDwStartYears = dwStartBar.length;
    if (totalDwStartYears > 20) {
      dwStartBar = [...dwStartBar]
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);
    }
    dwStartBar.sort((a, b) => a.year - b.year);

    // Line — P/L Over Time. Build a month-by-month cumulative running total
    // of cost & value across items that have a parsed `purchase_date` (top-
    // level) OR legacy `item_data["Purchase Date"]`. Items missing a date are
    // ignored per spec (they simply can't be plotted on a time axis). Dates
    // are coerced to a YYYY-MM bucket; anything that fails to parse is
    // dropped.
    //
    // For each month M from the earliest purchase month through max(today,
    // last purchase month):
    //   • Total Cost(M)  = Σ purchase_price for items whose purchase month ≤ M
    //   • Total Value(M) = Σ estimated_value for items whose purchase month ≤ M
    //   • P/L(M)         = Total Value(M) − Total Cost(M)
    // i.e. cumulative running totals, so each point represents the cellar's
    // state at month-end. Uses the canonical itemCost() / itemValue() helpers
    // so legacy + dynamic price fields are handled consistently with the rest
    // of the dashboard.
    const parsePurchaseMonth = (it) => {
      const raw = it.purchase_date ?? it.item_data?.["Purchase Date"];
      if (!raw) return null;
      const s = String(raw).trim();
      // Accept "YYYY-MM-DD" or "YYYY-MM" or any ISO-ish string Date can parse.
      const m = s.match(/^(\d{4})-(\d{2})/);
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]);
        if (mo >= 1 && mo <= 12 && y >= 1900 && y <= 2999) {
          return `${y}-${String(mo).padStart(2, "0")}`;
        }
      }
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return null;
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    };
    const datedItems = scopedItems
      .map((it) => ({ it, month: parsePurchaseMonth(it) }))
      .filter((x) => x.month);
    let plTimeline = [];
    if (datedItems.length > 0) {
      // Group items by their YYYY-MM purchase month so the cumulative running
      // total only needs a single pass.
      const perMonth = new Map(); // month → { cost, value }
      datedItems.forEach(({ it, month }) => {
        const val = _val(it);
        const c = val.total_cost || 0;
        const v = val.current_value || 0;
        const cur = perMonth.get(month) || { cost: 0, value: 0 };
        cur.cost += c;
        cur.value += v;
        perMonth.set(month, cur);
      });
      // Build a continuous month axis from first purchase month → max(last
      // purchase month, current month) so the timeline always ends "today".
      const monthKeys = Array.from(perMonth.keys()).sort();
      const [fy, fm] = monthKeys[0].split("-").map(Number);
      const now = new Date();
      const ly = now.getFullYear();
      const lm = now.getMonth() + 1;
      // Inclusive month iteration.
      let runningCost = 0;
      let runningValue = 0;
      let y = fy;
      let m = fm;
      // Guard against unbounded loops if `now` < first purchase month.
      const lastKey = monthKeys[monthKeys.length - 1];
      const [lyClamp, lmClamp] = (
        (ly > Number(lastKey.split("-")[0])) ||
        (ly === Number(lastKey.split("-")[0]) && lm >= Number(lastKey.split("-")[1]))
      ) ? [ly, lm] : [Number(lastKey.split("-")[0]), Number(lastKey.split("-")[1])];
      // Safety cap: at most 25 years of points (300 months).
      let safety = 300;
      while (safety-- > 0) {
        const key = `${y}-${String(m).padStart(2, "0")}`;
        const add = perMonth.get(key);
        if (add) {
          runningCost += add.cost;
          runningValue += add.value;
        }
        plTimeline.push({
          month: key,
          cost: sumQ2([runningCost]),
          value: sumQ2([runningValue]),
          pl: sumQ2([runningValue, -runningCost]),
        });
        if (y === lyClamp && m === lmClamp) break;
        m += 1;
        if (m > 12) { m = 1; y += 1; }
      }
      // If > 60 months (5 years) keep only the last 60 so the X-axis stays
      // legible.
      if (plTimeline.length > 60) plTimeline = plTimeline.slice(plTimeline.length - 60);
    }

    // Stacked bar — Region Maturity Curve. For every item with a Region AND
    // a parseable drinking window, classify it into Past / Ready / Future
    // buckets relative to the current calendar year:
    //   • Past:   end < currentYear
    //   • Ready:  start ≤ currentYear ≤ end
    //   • Future: start > currentYear
    // Items missing a window are skipped. If only `start` exists, end = start
    // (spec rule). Region is trimmed and grouped by exact string match;
    // empty/whitespace regions are dropped. Resulting rows are sorted by
    // total descending; truncated to top 12 if more exist.
    const maturityByRegion = new Map(); // region → { past, ready, future, total }
    scopedItems.forEach((it) => {
      const regRaw = it.region ?? it.item_data?.Region;
      if (!regRaw) return;
      const region = String(regRaw).trim();
      if (!region) return;
      const startRaw =
        it.drink_window_start ??
        it.item_data?.["Drink Window Start"] ??
        it.item_data?.["Drinking Window Start"];
      const endRaw =
        it.drink_window_end ??
        it.item_data?.["Drink Window End"] ??
        it.item_data?.["Drinking Window End"];
      const startN = startRaw === null || startRaw === undefined || startRaw === ""
        ? NaN : Number(startRaw);
      const endN = endRaw === null || endRaw === undefined || endRaw === ""
        ? NaN : Number(endRaw);
      let start = Number.isFinite(startN) ? Math.trunc(startN) : NaN;
      let end   = Number.isFinite(endN)   ? Math.trunc(endN)   : NaN;
      if (Number.isFinite(start) && !Number.isFinite(end)) end = start;
      if (!Number.isFinite(start) && Number.isFinite(end)) start = end;
      if (!Number.isFinite(start) || !Number.isFinite(end)) return;
      let bucket;
      if (end < currentYear) bucket = "past";
      else if (start > currentYear) bucket = "future";
      else bucket = "ready";
      const row = maturityByRegion.get(region) || { past: 0, ready: 0, future: 0, total: 0 };
      row[bucket] += 1;
      row.total += 1;
      maturityByRegion.set(region, row);
    });
    let regionMaturity = Array.from(maturityByRegion.entries())
      .map(([region, r]) => ({ region, ...r }))
      .sort((a, b) => b.total - a.total);
    const totalRegions = regionMaturity.length;
    if (totalRegions > 12) regionMaturity = regionMaturity.slice(0, 12);

    // Donut — Cellar Composition. Groups items by Wine Type (preferred) and
    // falls back to Category only when no item carries any wine-type signal.
    // Reads wine_type strictly from item_data — no more category-name
    // inference. Category names are now manual free-text (Feb 2026), so
    // scanning them for keywords like "Red" / "White" would misclassify
    // categories such as "Old Rose Vineyard". Items without an explicit
    // Wine Type / Type field simply drop from the composition chart and
    // the code falls through to the Category-grouping fallback below.
    const inferType = (it) => {
      const direct =
        it.wine_type ??
        it.item_data?.["Wine Type"] ??
        it.item_data?.Type;
      if (direct && String(direct).trim()) return String(direct).trim();
      return null;
    };
    const typeCounts = new Map();
    let typedItems = 0;
    scopedItems.forEach((it) => {
      const t = inferType(it);
      if (!t) return;
      // Sum bottles (itemQty), not rows — matches the donut's "Bottles"
      // center label. Rows with no quantity contribute 0 and are
      // effectively hidden from the composition until a qty is set.
      const q = itemQty(it);
      if (q <= 0) return;
      typeCounts.set(t, (typeCounts.get(t) || 0) + q);
      typedItems += q;
    });
    // If NO items carry a wine-type signal at all, fall back to grouping by
    // Category (using category_id so the deep-link uses the existing filter).
    let compositionDimension = "wine_type"; // or "category"
    let composition = [];
    let compositionTotal = 0;
    if (typedItems > 0) {
      composition = Array.from(typeCounts.entries())
        .map(([name, count]) => ({ name, count, key: name }))
        .sort((a, b) => b.count - a.count);
      compositionTotal = typedItems;
    } else {
      compositionDimension = "category";
      const byCat = new Map();
      scopedItems.forEach((it) => {
        // Same bottle-sum rule as the wine-type branch above.
        const q = itemQty(it);
        if (q <= 0) return;
        const id = it.category_id || "__uncat__";
        byCat.set(id, (byCat.get(id) || 0) + q);
      });
      composition = Array.from(byCat.entries())
        .map(([id, count]) => ({
          key: id,
          name: id === "__uncat__"
            ? "Uncategorised"
            : categories.find((c) => c.id === id)?.category_name || "Unknown",
          count,
        }))
        .sort((a, b) => b.count - a.count);
      compositionTotal = composition.reduce((s, r) => s + r.count, 0);
    }
    // Trim to top 6 slices for legibility; lump the rest into "Other".
    const COMP_LIMIT = 6;
    if (composition.length > COMP_LIMIT) {
      const head = composition.slice(0, COMP_LIMIT);
      const tail = composition.slice(COMP_LIMIT);
      composition = [
        ...head,
        { key: "__other__", name: `Other (${tail.length})`, count: tail.reduce((s, r) => s + r.count, 0) },
      ];
    }

    return {
      totalBottles, totalValue, totalCost, profit,
      catRows, topRegions, topGrapes,
      readyNow, pastEnd, currentYear,
      catBar, regionBar, grapePie, valueBar, dwTimeline,
      vintageBar, totalVintages,
      dwStartBar, totalDwStartYears,
      plTimeline,
      regionMaturity, totalRegions,
      composition, compositionDimension, compositionTotal,
    };
  }, [scopedItems, categories, currentProfile.id]);

  // ---- Navigation helpers -------------------------------------------------
  // Pin the currently-selected collection so chart deep-links open the
  // Items list scoped to the same data the Dashboard is summarising.
  const goTo = (extra = {}) => {
    const sp = new URLSearchParams();
    if (selectedCollectionId) sp.set("collection_id", selectedCollectionId);
    Object.entries(extra).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") sp.set(k, String(v));
    });
    navigate(`/items?${sp.toString()}`);
  };

  // Context-aware Back: shared resolver (state.from → nav tracker →
  // fallback "/"). Lets users hop back to whichever screen sent them here
  // without breaking deep links.
  const backHref = resolveBackHref({
    stateFrom: location.state?.from,
    currentPath: location.pathname + location.search,
    fallback: "/",
  });

  // ---- Render -------------------------------------------------------------
  // First-run empty state: warm welcome + CTA into the guided collection
  // wizard. Rendered ONLY once collections have finished loading so we
  // don't flash the empty state during the initial fetch. `<BottomNav>`
  // still renders so mobile users can navigate away without the header.
  if (!collectionsLoading && collections.length === 0) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bg))]" data-testid="dashboard-empty-root">
        <EmptyDashboard />
        <BottomNav current="dashboard" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="dashboard-page">
      <button
        type="button"
        onClick={() => navigate("/collections")}
        data-testid="dash-back"
        aria-label="Back to previous screen"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-3 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>

      {/* Select Collection (Task 2A — UI only). Scopes the Dashboard once
          chart / KPI wiring lands in 2B + 2C. Today, the underlying data
          source is unchanged. */}
      <div className="mb-4 flex items-center gap-2" data-testid="dash-collection-picker">
        <label
          htmlFor="dash-collection-select"
          className="text-xs vp-muted shrink-0 uppercase tracking-wider"
        >
          Select Collection
        </label>
        <select
          id="dash-collection-select"
          data-testid="dash-collection-select"
          value={selectedCollectionId}
          onChange={(e) => setSelectedCollectionId(e.target.value)}
          className="vp-input !py-1.5 !text-sm flex-1 min-w-0 max-w-xs"
        >
          {collections.length === 0 && <option value="">No collections</option>}
          {collections.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="mb-8">
        <div className="vp-label mb-2">Dashboard</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
          {(collections.find((c) => c.id === selectedCollectionId)?.name) || "Collection"} overview
        </h1>
        <p className="text-sm vp-muted mt-1">
          {loading
            ? "Loading metrics…"
            : `${kpis.totalBottles} ${(kpis.totalBottles === 1 ? currentProfile.itemLabel : currentProfile.itemLabelPlural).toLowerCase()} · summary as of ${new Date().toLocaleDateString()}`}
        </p>
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          testid="dash-total-bottles"
          Icon={Wine}
          label={`Total ${currentProfile.itemLabelPlural}`}
          value={kpis.totalBottles}
          description={`Total number of ${currentProfile.itemLabelPlural.toLowerCase()} currently held across every collection.`}
          loading={loading}
          onClick={() => goTo()}
        />
        <KpiCard
          testid="dash-total-value"
          Icon={Coins}
          label="Estimated Value (valued items only)"
          value={fmtMoney(kpis.totalValue)}
          description="Current estimated value of the bottles you still own (ex GST). Uses Manual → Market → most recent sale → buy price. Items without a valuation are excluded from Market Value and Profit calculations but still contribute to Total Cost."
          loading={loading}
          onClick={() => goTo({ sort: "value-desc" })}
        />
        <KpiCard
          testid="dash-total-cost"
          Icon={Coins}
          label="Total Cost (ex GST)"
          value={fmtMoney(kpis.totalCost)}
          description="Sum of what you paid for every bottle in your collection, inclusive of GST."
          loading={loading}
          onClick={() => goTo({ sort: "price-desc" })}
        />
        <KpiCard
          testid="dash-profit"
          Icon={kpis.valuedItemsCount === 0 ? Coins : (kpis.profit >= 0 ? TrendingUp : TrendingDown)}
          label={kpis.valuedItemsCount === 0 ? "Awaiting Valuation" : "Profit (Value − Cost)"}
          value={kpis.valuedItemsCount === 0
            ? "—"
            : `${kpis.profit >= 0 ? "+" : "−"}${fmtMoney(Math.abs(kpis.profit))}`}
          description={kpis.valuedItemsCount === 0
            ? "No items have a valuation set. Profit/Loss will appear once at least one item has a market value."
            : "Total Value minus Total Purchase Cost, on an ex-GST basis — combines realised sales profit and unrealised gain on remaining stock. Items without a valuation are excluded from Market Value and Profit calculations but still contribute to Total Cost."}
          tone={kpis.valuedItemsCount === 0
            ? "neutral"
            : (kpis.profit > 0 ? "gain" : kpis.profit < 0 ? "loss" : "neutral")}
          loading={loading}
          onClick={() => goTo({ sort: "value-desc" })}
        />
      </div>

      {/* Drinking Window Insights — wine-specific (only renders when the
          active analytics profile is "wine"). Other types don't have a
          drinking-window concept so we hide this entire KPI row for them. */}
      {currentProfile.id === "wine" && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <KpiCard
          testid="dash-ready-now"
          Icon={Clock}
          label={`Ready to Drink Now (${kpis.currentYear} ± 1)`}
          value={kpis.readyNow}
          description={`Bottles whose Drinking Window peak year is within ${kpis.currentYear - 1}–${kpis.currentYear + 1}. These are the wines to open before they slip past peak.`}
          subline={kpis.readyNow > 0 ? "Tap to see which bottles" : "Nothing peaking this window"}
          tone={kpis.readyNow > 0 ? "gold" : "neutral"}
          loading={loading}
          onClick={() =>
            goTo({ dwpmin: kpis.currentYear - 1, dwpmax: kpis.currentYear + 1 })
          }
        />
        <KpiCard
          testid="dash-past-end"
          Icon={AlertTriangle}
          label="Past End Window"
          value={kpis.pastEnd}
          description="Bottles whose Drinking Window End year has already passed — review before quality slips further."
          subline={kpis.pastEnd > 0 ? "Tap to review at-risk bottles" : "No bottles past their End year"}
          tone={kpis.pastEnd > 0 ? "loss" : "neutral"}
          loading={loading}
          onClick={() => goTo({ dwemax: kpis.currentYear - 1 })}
        />
      </div>
      )}

      {/* Breakdown lists — Category list is universal (every type has
          categories). Region + Grape lists are wine-specific and only
          render when the active profile is Wine. */}
      <div className={`grid grid-cols-1 gap-4 ${currentProfile.id === "wine" ? "sm:grid-cols-3" : ""}`}>
        <ListCard
          testid="dash-by-category"
          Icon={Tag}
          title={`${currentProfile.itemLabelPlural} by Category`}
          rows={stats.catRows}
          empty={`No categorised ${currentProfile.itemLabelPlural.toLowerCase()} yet`}
          rowKey={(r) => r.id}
          rowLabel={(r) => r.name}
          onRowClick={(r) =>
            r.id === "__uncat__"
              ? goTo({ category_id: "__UNCAT__" })
              : goTo({ category_id: r.id })
          }
          loading={loading}
        />
        {currentProfile.id === "wine" && (
        <ListCard
          testid="dash-by-region"
          Icon={MapPin}
          title="Bottles by Region"
          subtitle="Top 5"
          rows={stats.topRegions}
          empty="No region data yet"
          rowKey={(r) => r.name}
          rowLabel={(r) => r.name}
          onRowClick={(r) => goTo({ region: r.name })}
          loading={loading}
        />
        )}
        {currentProfile.id === "wine" && (
        <ListCard
          testid="dash-by-grape"
          Icon={Grape}
          title="Bottles by Grape"
          subtitle="Top 5"
          rows={stats.topGrapes}
          empty="No grape data yet"
          rowKey={(r) => r.name}
          rowLabel={(r) => r.name}
          onRowClick={(r) => goTo({ grape: r.name })}
          loading={loading}
        />
        )}
      </div>

      {/* ===== Charts ===== Recharts-driven, animations disabled.  Each chart
          lives in its own vp-card. Click handlers deep-link into Items list
          using the existing URL-backed filters — no new filter types.        */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dash-charts">
        {/* Items by Category — universal Bar chart (basic profile). */}
        <ChartCard
          testid="dash-chart-category"
          Icon={BarChart3}
          title={`${currentProfile.itemLabelPlural} by Category`}
          subtitle={stats.catRows.length > 8 ? `Top 8 of ${stats.catRows.length}` : null}
          isEmpty={stats.catBar.length === 0}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.catBar} margin={{ top: 8, right: 12, left: -16, bottom: 8 }}>
              <CartesianGrid strokeDasharray="2 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} />
              <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar
                dataKey="count"
                fill="hsl(var(--gold))"
                isAnimationActive={false}
                onClick={(d) => {
                  if (!d?.payload?.clickable) return;
                  goTo({ category_id: d.payload.id === "__uncat__" ? "__UNCAT__" : d.payload.id });
                }}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Bottles by Region — wine-specific Bar (top 5). */}
        {currentProfile.id === "wine" && (
        <ChartCard
          testid="dash-chart-region"
          Icon={BarChart3}
          title="Bottles by Region"
          subtitle="Top 5"
          isEmpty={stats.regionBar.length === 0}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.regionBar} margin={{ top: 8, right: 12, left: -16, bottom: 8 }}>
              <CartesianGrid strokeDasharray="2 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} />
              <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar
                dataKey="count"
                fill="hsl(var(--gain))"
                isAnimationActive={false}
                onClick={(d) => goTo({ region: d?.payload?.name })}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        )}

        {/* Bottles by Grape — wine-specific Donut (top 5). */}
        {currentProfile.id === "wine" && (
        <ChartCard
          testid="dash-chart-grape"
          Icon={PieIcon}
          title="Bottles by Grape"
          subtitle="Top 5"
          isEmpty={stats.grapePie.length === 0}
        >
          <ResponsiveContainer width="100%" height={240}>
            <PieChart
              onClick={(evt) => {
                const name = evt?.activePayload?.[0]?.name;
                if (name) goTo({ grape: name });
              }}
              onMouseDown={(evt) => {
                const name = evt?.activePayload?.[0]?.name;
                if (name) goTo({ grape: name });
              }}
              style={{ cursor: "pointer" }}
            >
              <Tooltip contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Pie
                data={stats.grapePie}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                isAnimationActive={false}
                stroke="hsl(var(--bg))"
                strokeWidth={2}
              >
                {stats.grapePie.map((entry, idx) => (
                  <Cell
                    key={entry.name}
                    fill={PIE_COLORS[idx % PIE_COLORS.length]}
                    data-testid={`dash-chart-grape-slice-${entry.name}`}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {stats.grapePie.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
              {stats.grapePie.map((g, idx) => (
                <button
                  key={g.name}
                  type="button"
                  onClick={() => goTo({ grape: g.name })}
                  data-testid={`dash-chart-grape-legend-${g.name}`}
                  className="flex items-center gap-2 truncate text-left px-1 py-0.5 rounded hover:bg-[hsl(var(--muted))]"
                >
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: PIE_COLORS[idx % PIE_COLORS.length] }} />
                  <span className="truncate">{g.name}</span>
                  <span className="vp-muted ml-auto font-mono tabular-nums">{g.count}</span>
                </button>
              ))}
            </div>
          )}
        </ChartCard>
        )}

        {/* Value Distribution — Bar (4 bands). No price-range filter exists,
            so a band click navigates to /items sorted by value-desc. */}
        <ChartCard
          testid="dash-chart-value"
          Icon={BarChart3}
          title="Value Distribution"
          subtitle="Using item value"
          isEmpty={stats.valueBar.every((b) => b.count === 0)}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.valueBar} margin={{ top: 8, right: 12, left: -16, bottom: 8 }}>
              <CartesianGrid strokeDasharray="2 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} />
              <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar
                dataKey="count"
                fill="hsl(var(--gold-strong))"
                isAnimationActive={false}
                onClick={() => goTo({ sort: "value-desc" })}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Drinking Window timeline — wine-specific Line (full-width on lg+). */}
        {currentProfile.id === "wine" && (
        <div className="lg:col-span-2">
          <ChartCard
            testid="dash-chart-timeline"
            Icon={LineIcon}
            title="Drinking Window — Peak Year Timeline"
            subtitle={
              stats.dwTimeline.length > 0
                ? `${stats.dwTimeline[0].year} – ${stats.dwTimeline[stats.dwTimeline.length - 1].year}`
                : null
            }
            isEmpty={stats.dwTimeline.length === 0}
            empty="No Drinking Window Peak data yet — add Peak years to your bottles to populate this timeline."
          >
            <ResponsiveContainer width="100%" height={240}>
              <LineChart
                data={stats.dwTimeline}
                margin={{ top: 8, right: 16, left: -16, bottom: 8 }}
                onClick={(evt) => {
                  const y = evt?.activePayload?.[0]?.payload?.year;
                  if (y) goTo({ dwpmin: y, dwpmax: y });
                }}
                onMouseDown={(evt) => {
                  const y = evt?.activePayload?.[0]?.payload?.year;
                  if (y) goTo({ dwpmin: y, dwpmax: y });
                }}
                style={{ cursor: "pointer" }}
              >
                <CartesianGrid strokeDasharray="2 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="year" tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} />
                <Tooltip cursor={{ stroke: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="hsl(var(--gold-strong))"
                  strokeWidth={2}
                  isAnimationActive={false}
                  dot={<TimelineDot onYearClick={(y) => goTo({ dwpmin: y, dwpmax: y })} />}
                  activeDot={{ r: 6, stroke: "hsl(var(--gold-strong))", strokeWidth: 2, fill: "hsl(var(--bg))" }}
                />
              </LineChart>
            </ResponsiveContainer>
            {stats.dwTimeline.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]" data-testid="dash-chart-timeline-years">
                {stats.dwTimeline
                  .filter((p) => p.count > 0)
                  .map((p) => (
                    <button
                      key={p.year}
                      type="button"
                      onClick={() => goTo({ dwpmin: p.year, dwpmax: p.year })}
                      data-testid={`dash-chart-timeline-year-${p.year}`}
                      className="px-2 py-0.5 rounded border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] flex items-center gap-1.5"
                    >
                      <span className="font-mono tabular-nums">{p.year}</span>
                      <span className="vp-muted">·</span>
                      <span className="vp-muted">{p.count}</span>
                    </button>
                  ))}
              </div>
            )}
          </ChartCard>
        </div>
        )}

        {/* Vintage Value Distribution — W/W/S Bar (full-width on lg+). */}
        {(currentProfile.id === "wine" || currentProfile.id === "whisky") && (
        <div className="lg:col-span-2">
          <ChartCard
            testid="dash-chart-vintage"
            Icon={BarChart3}
            title={currentProfile.id === "whisky" ? "Distillation Year Value Distribution" : "Vintage Value Distribution"}
            subtitle={
              stats.totalVintages > 20
                ? `Top 20 of ${stats.totalVintages} · Total value by ${currentProfile.id === "whisky" ? "distillation" : "vintage"} year`
                : `Total value by ${currentProfile.id === "whisky" ? "distillation" : "vintage"} year`
            }
            isEmpty={stats.vintageBar.length === 0}
            empty={currentProfile.id === "whisky"
              ? "No distillation year valuation data yet — add a Distillation Year and a purchase or estimated value to your bottles to populate this chart."
              : "No vintage valuation data yet — add a Vintage year and a purchase or estimated value to your bottles to populate this chart."}
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.vintageBar} margin={{ top: 8, right: 12, left: -16, bottom: 8 }}>
                <CartesianGrid strokeDasharray="2 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }}
                  interval={0}
                  angle={-30}
                  textAnchor="end"
                  height={50}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted))" }}
                  contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Bar
                  dataKey="value"
                  fill="hsl(var(--gold-strong))"
                  isAnimationActive={false}
                  onClick={(d) => {
                    const y = d?.payload?.year;
                    if (!y) return;
                    if (currentProfile.id === "whisky") goTo({ dymin: y, dymax: y });
                    else goTo({ vmin: y, vmax: y });
                  }}
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        )}

        {/* Drinking Window Timeline — wine-specific Bar (full-width on lg+). */}
        {currentProfile.id === "wine" && (
        <div className="lg:col-span-2">
          <ChartCard
            testid="dash-chart-dw-start"
            Icon={BarChart3}
            title="Drinking Window Timeline"
            subtitle={
              stats.totalDwStartYears > 20
                ? `Top 20 of ${stats.totalDwStartYears} · Bottles reaching peak by year`
                : "Bottles reaching peak by year"
            }
            isEmpty={stats.dwStartBar.length === 0}
            empty="No Drinking Window Start data yet — add a Drinking Window Start year to your bottles to populate this chart."
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.dwStartBar} margin={{ top: 8, right: 12, left: -16, bottom: 8 }}>
                <CartesianGrid strokeDasharray="2 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }}
                  interval={0}
                  angle={-30}
                  textAnchor="end"
                  height={50}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted))" }}
                  contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Bar
                  dataKey="count"
                  fill="hsl(var(--gain))"
                  isAnimationActive={false}
                  onClick={(d) => {
                    const y = d?.payload?.year;
                    if (y) goTo({ dwsmin: y, dwsmax: y });
                  }}
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        )}

        {/* P/L Over Time — universal Line (full-width on lg+). Reads
            purchase_date which exists on every Item type. Counts toward the
            basic chart set (value-over-time kind). */}
        <div className="lg:col-span-2">
          <ChartCard
            testid="dash-chart-pl-time"
            Icon={LineIcon}
            title="P/L Over Time"
            subtitle={
              stats.plTimeline.length > 0
                ? `Cellar value vs cost by month · ${stats.plTimeline[0].month} – ${stats.plTimeline[stats.plTimeline.length - 1].month}`
                : "Cellar value vs cost by month"
            }
            isEmpty={stats.plTimeline.length === 0}
            empty="No purchase-date data yet — add a Purchase Date to your bottles to populate this chart."
          >
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={stats.plTimeline}
                margin={{ top: 8, right: 16, left: -8, bottom: 8 }}
                onClick={(evt) => {
                  const m = evt?.activePayload?.[0]?.payload?.month;
                  if (m) goTo({ purchased_before: m });
                }}
                onMouseDown={(evt) => {
                  const m = evt?.activePayload?.[0]?.payload?.month;
                  if (m) goTo({ purchased_before: m });
                }}
                style={{ cursor: "pointer" }}
              >
                <CartesianGrid strokeDasharray="2 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }}
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }}
                  tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`}
                />
                <Tooltip
                  cursor={{ stroke: "hsl(var(--muted))" }}
                  contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, name) => [fmtMoney(v), name]}
                  labelFormatter={(m) => `Month: ${m}`}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  name="Total Value"
                  stroke="hsl(var(--gold-strong))"
                  strokeWidth={2}
                  isAnimationActive={false}
                  dot={false}
                  activeDot={{ r: 5, stroke: "hsl(var(--gold-strong))", strokeWidth: 2, fill: "hsl(var(--bg))" }}
                />
                <Line
                  type="monotone"
                  dataKey="cost"
                  name="Total Cost"
                  stroke="hsl(var(--muted-fg))"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  isAnimationActive={false}
                  dot={false}
                  activeDot={{ r: 4, stroke: "hsl(var(--muted-fg))", strokeWidth: 2, fill: "hsl(var(--bg))" }}
                />
                <Line
                  type="monotone"
                  dataKey="pl"
                  name="Unrealised P/L"
                  stroke="hsl(var(--gain))"
                  strokeWidth={1.5}
                  isAnimationActive={false}
                  dot={false}
                  activeDot={{ r: 4, stroke: "hsl(var(--gain))", strokeWidth: 2, fill: "hsl(var(--bg))" }}
                />
              </LineChart>
            </ResponsiveContainer>
            {stats.plTimeline.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-4 text-[11px]" data-testid="dash-chart-pl-time-legend">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-[2px] bg-[hsl(var(--gold-strong))]" />
                  <span>Total Value</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-[2px] border-t border-dashed border-[hsl(var(--muted-fg))]" />
                  <span>Total Cost</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-[2px] bg-[hsl(var(--gain))]" />
                  <span>Unrealised P/L</span>
                </span>
              </div>
            )}
          </ChartCard>
        </div>

        {/* Region Maturity Curve — wine-specific Stacked Bar (full-width on lg+). */}
        {currentProfile.id === "wine" && (
        <div className="lg:col-span-2">
          <ChartCard
            testid="dash-chart-region-maturity"
            Icon={BarChart3}
            title="Region Maturity Curve"
            subtitle={
              stats.totalRegions > 12
                ? `Top 12 of ${stats.totalRegions} · Past / Ready / Future by region`
                : "Past / Ready / Future by region"
            }
            isEmpty={stats.regionMaturity.length === 0}
            empty="No regions with drinking-window data yet — add a Region and a Drinking Window to your bottles to populate this chart."
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.regionMaturity} margin={{ top: 8, right: 12, left: -16, bottom: 24 }}>
                <CartesianGrid strokeDasharray="2 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="region"
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={60}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-fg))" }} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted))" }}
                  contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Bar
                  dataKey="past"
                  name="Past"
                  stackId="maturity"
                  fill="hsl(var(--c-red))"
                  fillOpacity={0.85}
                  isAnimationActive={false}
                  onClick={(d) => {
                    const r = d?.payload?.region;
                    if (r) goTo({ region: r, dwemax: stats.currentYear - 1 });
                  }}
                  cursor="pointer"
                />
                <Bar
                  dataKey="ready"
                  name="Ready"
                  stackId="maturity"
                  fill="hsl(var(--c-gold))"
                  isAnimationActive={false}
                  onClick={(d) => {
                    const r = d?.payload?.region;
                    if (r) goTo({ region: r, dwsmax: stats.currentYear, dwemin: stats.currentYear });
                  }}
                  cursor="pointer"
                />
                <Bar
                  dataKey="future"
                  name="Future"
                  stackId="maturity"
                  fill="hsl(var(--c-blue))"
                  fillOpacity={0.85}
                  isAnimationActive={false}
                  onClick={(d) => {
                    const r = d?.payload?.region;
                    if (r) goTo({ region: r, dwsmin: stats.currentYear + 1 });
                  }}
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
            {stats.regionMaturity.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-4 text-[11px]" data-testid="dash-chart-region-maturity-legend">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm" style={{ background: "hsl(var(--c-red))", opacity: 0.85 }} />
                  <span>Past</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm" style={{ background: "hsl(var(--c-gold))" }} />
                  <span>Ready</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm" style={{ background: "hsl(var(--c-blue))", opacity: 0.85 }} />
                  <span>Future</span>
                </span>
              </div>
            )}
          </ChartCard>
        </div>
        )}

        {/* Cellar Composition — wine-specific Donut (full-width on lg+). */}
        {currentProfile.id === "wine" && (
        <div className="lg:col-span-2">
          <ChartCard
            testid="dash-chart-composition"
            Icon={PieIcon}
            title="Cellar Composition"
            subtitle={
              stats.compositionDimension === "wine_type"
                ? "Breakdown by wine type"
                : "Breakdown by category"
            }
            isEmpty={stats.composition.length === 0}
            empty="No wine-type or category data yet — categorise your bottles or set a Wine Type to populate this chart."
          >
            <div className="relative">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart
                  onClick={(evt) => {
                    const slice = evt?.activePayload?.[0]?.payload;
                    if (!slice) return;
                    if (slice.key === "__other__") return;
                    if (stats.compositionDimension === "wine_type") {
                      goTo({ wine_type: slice.name });
                    } else {
                      goTo({ category_id: slice.key === "__uncat__" ? "__UNCAT__" : slice.key });
                    }
                  }}
                  onMouseDown={(evt) => {
                    const slice = evt?.activePayload?.[0]?.payload;
                    if (!slice) return;
                    if (slice.key === "__other__") return;
                    if (stats.compositionDimension === "wine_type") {
                      goTo({ wine_type: slice.name });
                    } else {
                      goTo({ category_id: slice.key === "__uncat__" ? "__UNCAT__" : slice.key });
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Tooltip contentStyle={{ background: "hsl(var(--bg-elevated))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Pie
                    data={stats.composition}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={110}
                    isAnimationActive={false}
                    stroke="hsl(var(--bg))"
                    strokeWidth={2}
                  >
                    {stats.composition.map((entry, idx) => (
                      <Cell
                        key={entry.key}
                        fill={PIE_COLORS[idx % PIE_COLORS.length]}
                        data-testid={`dash-chart-composition-slice-${entry.name}`}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              {/* Center label — total bottles covered by the donut. */}
              <div
                className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
                data-testid="dash-chart-composition-center"
              >
                <span className="text-2xl font-semibold tabular-nums">
                  {stats.compositionTotal}
                </span>
                <span className="text-[10px] uppercase tracking-wider vp-muted">
                  {stats.compositionTotal === 1 ? "Bottle" : "Bottles"}
                </span>
              </div>
            </div>
            {stats.composition.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                {stats.composition.map((c, idx) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => {
                      if (c.key === "__other__") return;
                      if (stats.compositionDimension === "wine_type") {
                        goTo({ wine_type: c.name });
                      } else {
                        goTo({ category_id: c.key === "__uncat__" ? "__UNCAT__" : c.key });
                      }
                    }}
                    data-testid={`dash-chart-composition-legend-${c.name}`}
                    className="flex items-center gap-2 truncate text-left px-1 py-0.5 rounded hover:bg-[hsl(var(--muted))] disabled:opacity-60"
                    disabled={c.key === "__other__"}
                  >
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: PIE_COLORS[idx % PIE_COLORS.length] }} />
                    <span className="truncate">{c.name}</span>
                    <span className="vp-muted ml-auto font-mono tabular-nums">{c.count}</span>
                  </button>
                ))}
              </div>
            )}
          </ChartCard>
        </div>
        )}

        {/* === Premium Analytics (Task D) =================================
            Type-specific premium charts surfaced from the profile registry.
            Renders one locked placeholder card per `currentProfile.premiumCharts`
            entry. Cards whose `field` is not present on ANY item in the
            current scope are hidden gracefully (per spec). When the user is
            premium (`isPremium`), the lock overlay would be removed — for
            now `isPremium` is hardcoded false until auth lands.
            For the wine profile, the rich implementations above already
            cover the same premium-titled charts (grandfathered), so we
            skip the duplicate locked-card row. */}
        {currentProfile.id !== "wine" && currentProfile.premiumCharts
          .filter((c) => {
            if (!c.field) return true; // canonical-field chart, always eligible
            return scopedItems.some((it) => {
              const v = it?.item_data?.[c.field];
              return v !== undefined && v !== null && v !== "";
            });
          })
          .map((c) => (
            <div key={c.id} className="lg:col-span-2">
              <LockedChartCard
                testid={`dash-chart-locked-${c.id}`}
                title={c.title}
                kind={c.kind}
                locked={!isPremium}
              />
            </div>
          ))}
      </div>

      {/* GST disclosure footer */}
      <p
        className="mt-6 text-[11px] vp-muted text-center"
        data-testid="dash-gst-footer"
      >
        All values shown Ex GST. Purchase prices shown Inc GST.
      </p>

      <BottomNav current="dashboard" />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function KpiCard({ testid, Icon, label, value, subline, description, tone = "neutral", loading, onClick }) {
  const toneClass =
    tone === "gain"
      ? "text-[hsl(var(--gain))]"
      : tone === "loss"
        ? "text-[hsl(var(--loss))]"
        : tone === "gold"
          ? "text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
          : "text-[hsl(var(--fg))]";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={description || label}
      aria-label={description ? `${label} — ${description}` : label}
      className="vp-card vp-card-hover p-5 text-left flex flex-col gap-2 cursor-pointer"
    >
      <div className="flex items-center gap-2 vp-muted">
        <Icon size={14} />
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className={`text-2xl font-semibold tracking-tight ${toneClass}`}>
        {loading ? "—" : value}
      </div>
      {subline && (
        <div className="text-[11px] vp-muted">{subline}</div>
      )}
    </button>
  );
}

function ListCard({ testid, Icon, title, subtitle, rows, empty, rowKey, rowLabel, onRowClick, loading }) {
  return (
    <div className="vp-card p-5 flex flex-col" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={14} className="vp-muted" />
          <h2 className="text-sm font-semibold">{title}</h2>
        </div>
        {subtitle && (
          <span className="text-[10px] uppercase tracking-wider vp-muted">{subtitle}</span>
        )}
      </div>
      {loading ? (
        <div className="space-y-2">
          <div className="vp-skeleton h-5 rounded" />
          <div className="vp-skeleton h-5 rounded" />
          <div className="vp-skeleton h-5 rounded" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs vp-muted">{empty}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map((r) => (
            <li key={rowKey(r)}>
              <button
                type="button"
                onClick={() => onRowClick(r)}
                data-testid={`${testid}-row-${rowKey(r)}`}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-[hsl(var(--muted))] text-left"
              >
                <span className="truncate">{rowLabel(r)}</span>
                <span className="text-xs vp-muted font-mono tabular-nums">{r.count}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------
   ChartCard — vp-card wrapper used by every chart on the Dashboard.
   Provides the heading row + empty-state messaging so the individual
   chart blocks above stay focused on data.
   ------------------------------------------------------------------ */
function ChartCard({ testid, Icon, title, subtitle, children, isEmpty, empty }) {
  return (
    <div className="vp-card p-5 flex flex-col" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="vp-muted" />}
          <h2 className="text-sm font-semibold">{title}</h2>
        </div>
        {subtitle && (
          <span className="text-[10px] uppercase tracking-wider vp-muted">{subtitle}</span>
        )}
      </div>
      {isEmpty ? (
        <p className="text-xs vp-muted">
          {empty || "No data yet — add items to populate this chart."}
        </p>
      ) : (
        children
      )}
    </div>
  );
}


/* ------------------------------------------------------------------
   TimelineDot — custom Line dot that handles per-point clicks. Hoisted
   to a module-level component so React keeps the same component type
   across re-renders (avoids react/no-unstable-nested-components).
   ------------------------------------------------------------------ */
function TimelineDot({ cx, cy, payload, onYearClick }) {
  const y = payload?.year;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      stroke="hsl(var(--gold-strong))"
      strokeWidth={2}
      fill="hsl(var(--bg))"
      style={{ cursor: "pointer" }}
      onClick={() => y && onYearClick(y)}
      data-testid={`dash-timeline-dot-${y}`}
    />
  );
}


/* ------------------------------------------------------------------ */
/* LockedChartCard — Premium analytics placeholder rendered for
 * type-specific premium charts that are visible-but-locked behind the
 * Premium plan. Shows a faint preview pattern, the chart title, and a
 * "Upgrade to Premium to unlock {chartName}" overlay (per Task D spec).
 * When `locked` is false (premium user), this would render the real
 * chart — left as a TODO for when chart-rendering by `kind` is wired.
 * ------------------------------------------------------------------ */
function LockedChartCard({ testid, title, kind, locked = true }) {
  return (
    <div
      className="vp-card p-5 relative overflow-hidden"
      data-testid={testid}
      data-locked={locked ? "true" : "false"}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2 vp-muted">
          <Lock size={14} />
          <span className="text-[11px] uppercase tracking-wider font-medium">{title}</span>
        </div>
        <span className="text-[10px] uppercase tracking-wider vp-muted">Premium</span>
      </div>
      {/* Faint preview pattern — gives the card visual height without
          implying any specific data. Kept generic across kinds. */}
      <div
        aria-hidden
        className="h-[180px] w-full rounded-md bg-[hsl(var(--muted))] opacity-30"
        data-testid={`${testid}-preview`}
        data-kind={kind}
      />
      {locked && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 backdrop-blur-[2px] bg-[hsl(var(--bg))]/55"
          data-testid={`${testid}-overlay`}
        >
          <Lock size={20} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] mb-2" />
          <p className="text-sm font-medium" data-testid={`${testid}-overlay-copy`}>
            Upgrade to Premium to unlock {title}
          </p>
        </div>
      )}
    </div>
  );
}
